import {
  Chip,
  Divider,
  HintTarget,
  Icon,
  Link,
  PresenceIndicator,
  Text,
  Tooltip,
} from 'src/design-system/ui'
import PortalModeSwitcher, {
  type PortalMode,
} from 'src/components/businessPortal/PortalModeSwitcher'

import { BusinessPortalWorkspaceControls } from './BusinessPortalWorkspaceControls'
import type {
  BusinessPortalNavItem,
  BusinessPortalWorkspaceAccess,
} from './useBusinessPortal'

export const BusinessPortalNavigation = ({
  agentModeAvailable,
  canSelectWorkspace,
  currentPm,
  debugNavItem,
  expanded,
  navItems,
  onModeChange,
  onNavClick,
  onPmChange,
  pmNamesLoading,
  workspaces,
}: {
  agentModeAvailable: boolean
  canSelectWorkspace: boolean
  currentPm: string | null
  debugNavItem: BusinessPortalNavItem | null
  expanded: boolean
  navItems: BusinessPortalNavItem[]
  onModeChange: (mode: PortalMode) => void
  onNavClick: (href: string) => void
  onPmChange: (nextPm: string | null) => void
  pmNamesLoading: boolean
  workspaces: BusinessPortalWorkspaceAccess[]
}) => {
  const hasWorkspace = workspaces.some(
    (workspace) => workspace.pmName === currentPm
  )

  return (
    <div className="ds:flex ds:h-full ds:min-h-0 ds:flex-col">
      {agentModeAvailable && (
        <div className={expanded ? 'ds:px-4 ds:py-4' : 'ds:px-2 ds:py-4'}>
          <PortalModeSwitcher
            expanded={expanded}
            mode="portal"
            onChange={onModeChange}
          />
        </div>
      )}
      <div className="ds:min-h-0 ds:flex-1 ds:overflow-y-auto ds:py-4">
        <ul className="ds:m-0 ds:grid ds:list-none ds:gap-1 ds:px-2">
          {navItems.map((item) => {
            const label = `${item.label}${item.isNew ? ', New' : ''}`
            const content = (
              <>
                <PresenceIndicator
                  active={Boolean(item.isNew && !expanded)}
                  label="New"
                >
                  <Icon icon={item.icon} size="sm" tone="current" />
                </PresenceIndicator>
                {expanded &&
                  (item.isDisabled ? (
                    <span className="ui-text ui-type-body">{item.label}</span>
                  ) : (
                    <Text variant="body">{item.label}</Text>
                  ))}
                {expanded && item.isNew && <Chip size="sm">New</Chip>}
              </>
            )
            const classes = expanded
              ? 'ds:flex ds:min-h-12 ds:items-center ds:gap-3 ds:px-3'
              : 'ds:flex ds:min-h-12 ds:items-center ds:justify-center'
            if (item.isDisabled) {
              const reason = hasWorkspace
                ? "You don't have access."
                : 'Select a workspace first.'
              return (
                <li key={item.id}>
                  <Tooltip content={reason} placement="right">
                    <HintTarget
                      role="link"
                      aria-disabled="true"
                      aria-label={`${label}: ${reason}`}
                      className={`${classes} ds:text-app-disabled-foreground`}
                    >
                      {content}
                    </HintTarget>
                  </Tooltip>
                </li>
              )
            }
            const link = (
              <Link
                variant="navigation"
                href={item.href}
                onNavigate={onNavClick}
                aria-label={label}
                aria-current={item.isActive ? 'page' : undefined}
                className={classes}
              >
                {content}
              </Link>
            )
            return (
              <li key={item.id}>
                {expanded ? (
                  link
                ) : (
                  <Tooltip content={label} placement="right">
                    {link}
                  </Tooltip>
                )}
              </li>
            )
          })}
        </ul>
      </div>
      {expanded && (canSelectWorkspace || debugNavItem) && (
        <>
          <Divider spacing="none" />
          <BusinessPortalWorkspaceControls
            canSelectWorkspace={canSelectWorkspace}
            currentPm={currentPm}
            debugNavItem={debugNavItem}
            onDebugClick={onNavClick}
            onPmChange={onPmChange}
            pmNamesLoading={pmNamesLoading}
            workspaces={workspaces}
          />
        </>
      )}
    </div>
  )
}
